import { Component, Suspense } from "react";
import type { ComponentType, ReactNode } from "react";
import { useLocation } from "react-router";
import * as Sentry from "@sentry/react";
import DefaultShell from "../features/shell/Shell.jsx";
import NotFoundPage from "../features/not-found/NotFoundPage.jsx";
import ErrorState from "../features/error/ErrorState";

type ThrownError = (Error & { status?: number }) | null;

type ShellProps = { children?: ReactNode; isLoading?: boolean };

type InnerProps = {
  children: ReactNode;
  pathname: string;
  Shell: ComponentType<ShellProps>;
};

/**
 * Error boundary for a route subtree. React error boundaries must be class
 * components; we hand-roll one (rather than `Sentry.ErrorBoundary`) so we own the
 * 404-vs-error branch, the reset, and the Sentry call.
 *
 * - A thrown `error.status === 404` renders `NotFoundPage` — a 404 reaching a UI
 *   boundary is always a bug (broken link / stale id), so we ALSO report to Sentry.
 * - Any other throw renders `ErrorState` with a retry that resets this boundary
 *   (SWR then revalidates the failed key on remount).
 * - Both render in neutral chrome (`Shell`), never the route's own data-dependent
 *   Layout, and inside their own `Suspense` because `NotFoundPage` itself reads a model.
 * - Resets when `pathname` changes so a 404 clears on navigation and e.g.
 *   `/library/1 → /library/2` (2 missing) re-throws instead of showing stale content.
 */
class RouteBoundaryInner extends Component<InnerProps, { error: ThrownError }> {
  state: { error: ThrownError } = { error: null };

  static getDerivedStateFromError(error: ThrownError) {
    return { error };
  }

  componentDidCatch(error: ThrownError, info: { componentStack?: string }) {
    Sentry.captureException(error, {
      tags: { boundary: "route", status: error?.status },
      extra: {
        pathname: this.props.pathname,
        componentStack: info?.componentStack,
      },
    });
  }

  componentDidUpdate(prevProps: InnerProps) {
    if (prevProps.pathname !== this.props.pathname && this.state.error) {
      this.setState({ error: null });
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error) {
      const { Shell } = this.props;
      const content =
        error.status === 404 ? (
          <NotFoundPage />
        ) : (
          <ErrorState onRetry={this.reset} />
        );
      return (
        <Suspense fallback={<Shell isLoading />}>
          <Shell>{content}</Shell>
        </Suspense>
      );
    }
    return this.props.children;
  }
}

/**
 * Functional wrapper so the class can read `useLocation` for pathname-based resets.
 * `Shell` is the neutral chrome the 404/error view renders in.
 */
const RouteBoundary = ({
  children,
  Shell = DefaultShell as ComponentType<ShellProps>,
}: {
  children: ReactNode;
  Shell?: ComponentType<ShellProps>;
}) => {
  const { pathname } = useLocation();
  return (
    <RouteBoundaryInner pathname={pathname} Shell={Shell}>
      {children}
    </RouteBoundaryInner>
  );
};

export default RouteBoundary;
